import { useMemo, useState } from 'react';
import { Checkbox, Input, Modal, Select, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import callApi from '../../Api/config';
import { DOCUMITRA_ORDER_APPLICATION } from '../../constants/EntityName';
import { DOCUMENT_TYPE_TAXONOMY } from '../../constants/DocumentTypes';
import { toastError, toastSuccess } from '../../service/Toast';
import htmlToPlainText from '../../service/htmlToPlainText';
import { InvertFillButton } from '../InvertFillButton';

const { Text } = Typography;

const ADDITIONAL_INFO_STATUS = 'Additional Information Required';

interface DocumentReminderPanelProps {
  /** Database id of the order (formDetails.id) */
  orderId?: string;
  /** Display order id, e.g. DOC/AUG/24/246510 */
  orderDisplayId?: string;
  member: any;
  applicantEmail?: string;
  /** Document category ids ticked on the "No documents" cards */
  selectedDocumentIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Called when the backend moved the member to "Additional Information Required" */
  onStatusUpdated?: (memberId: string, status: string) => void;
  onReminderSent?: () => void;
}

const normalizeStatus = (status?: string) =>
  String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/**
 * "Send Document Reminder" panel (CMS Documents tab).
 * Documents ticked on the checklist cards + any extra document types are emailed to the
 * applicant with the remark.
 */
const DocumentReminderPanel = ({
  orderId,
  orderDisplayId,
  member,
  applicantEmail,
  selectedDocumentIds,
  onSelectionChange,
  onStatusUpdated,
  onReminderSent,
}: DocumentReminderPanelProps) => {
  const [remark, setRemark] = useState<string>('');
  const [extraDocumentTypes, setExtraDocumentTypes] = useState<string[]>([]);
  const [updateStatus, setUpdateStatus] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const memberDocuments: any[] = useMemo(
    () => (Array.isArray(member?.documents) ? member.documents : []),
    [member]
  );
  const missingDocumentIds = useMemo(
    () =>
      memberDocuments
        .filter(doc => !Array.isArray(doc?.photos) || doc.photos.length === 0)
        .map(doc => String(doc?.id)),
    [memberDocuments]
  );
  const selectedCategories = memberDocuments.filter(doc =>
    selectedDocumentIds.includes(String(doc?.id))
  );
  const documentNames = [...selectedCategories.map(doc => doc.name), ...extraDocumentTypes];
  const totalSelected = selectedCategories.length + extraDocumentTypes.length;
  const isAlreadyAdditionalInfo =
    normalizeStatus(member?.status) === normalizeStatus(ADDITIONAL_INFO_STATUS);

  const handleClear = () => {
    onSelectionChange(
      selectedDocumentIds.filter(id => !selectedCategories.some(doc => String(doc.id) === id))
    );
    setExtraDocumentTypes([]);
    setRemark('');
  };

  const sendReminder = async (): Promise<boolean> => {
    if (!orderId || !member?.id) {
      toastError('Order or member details missing — cannot send reminder');
      return false;
    }
    try {
      setSending(true);
      const payload = {
        memberId: member.id,
        documents: [
          ...selectedCategories.map(doc => ({
            documentId: String(doc.id),
            name: doc.name,
            // Plan upload instructions travel with the category as its remark
            remarks: htmlToPlainText(doc?.description) || undefined,
          })),
          ...extraDocumentTypes.map(name => ({ name })),
        ],
        additionalInstructions: htmlToPlainText(remark) || undefined,
        updateStatus: !isAlreadyAdditionalInfo && updateStatus,
      };
      const res = await callApi(
        `${DOCUMITRA_ORDER_APPLICATION}/${orderId}/missing-documents-email`,
        'POST',
        payload
      );
      if (res?.status || res?.data?.statusCode === 200) {
        const result = res?.data?.data || {};
        toastSuccess(
          `Document reminder email sent to ${result.sentTo || applicantEmail || 'the applicant'}${
            result.statusUpdated ? `. Status set to ${ADDITIONAL_INFO_STATUS}.` : ''
          }`
        );
        if (result.statusUpdated && result.memberStatus) {
          onStatusUpdated?.(member.id, result.memberStatus);
        }
        setRemark('');
        setExtraDocumentTypes([]);
        onSelectionChange(
          selectedDocumentIds.filter(id => !selectedCategories.some(doc => String(doc.id) === id))
        );
        onReminderSent?.();
        return true;
      } else {
        toastError(res?.data?.message || 'Failed to send document reminder');
        return false;
      }
    } catch (error) {
      console.error('Error sending document reminder:', error);
      toastError('Failed to send document reminder');
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleSendClick = () => {
    if (totalSelected === 0) {
      toastError('Select at least one document from the checklist or the document list');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    if (await sendReminder()) {
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <div className="" style={{ marginTop: '24px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: '#eff6ff',
          }}
        >
          <div>
            <Text strong style={{ display: 'block', color: '#004195', paddingBottom: '8px' }}>
              Additional Documents:
            </Text>
            <Select
              className="document-reminder-control"
              mode="multiple"
              value={extraDocumentTypes}
              onChange={(values: string[]) => setExtraDocumentTypes(values)}
              placeholder="Add other document types"
              style={{ width: '100%' }}
              showSearch
              allowClear
              filterOption={(input, option) =>
                String((option as any)?.plainLabel || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              dropdownClassName="multiselect-checkbox-dropdown"
              optionFilterProp="plainLabel"
              options={DOCUMENT_TYPE_TAXONOMY.flatMap(category => category.types).map(type => ({
                label: type,
                plainLabel: type,
                value: type,
              }))}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px', color: '#004195' }}>
              Additional Instructions / Remark:
            </Text>
            <Input.TextArea
              className="document-reminder-control"
              value={remark}
              onChange={event => setRemark(event.target.value)}
              placeholder="Enter a reminder remark / instructions for the applicant"
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={4000}
            />
          </div>
        </div>

        <div style={{ marginTop: '12px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <InvertFillButton
                type="button"
                onClick={handleClear}
                variant="red"
                fontWeight="medium"
              >
                Clear
              </InvertFillButton>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ marginLeft: 'auto' }}>
                {isAlreadyAdditionalInfo ? (
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    Application status is already {ADDITIONAL_INFO_STATUS}.
                  </Text>
                ) : (
                  <Checkbox
                    checked={updateStatus}
                    onChange={e => setUpdateStatus(e.target.checked)}
                  >
                    <span style={{ fontSize: '14px' }}>
                      Also set the application status to <strong>{ADDITIONAL_INFO_STATUS}</strong>
                      {/* {member?.status ? ` (currently: ${member.status})` : ''} */}
                    </span>
                  </Checkbox>
                )}
              </div>
              <InvertFillButton
                variant="blue"
                size="md"
                fontWeight="semibold"
                onClick={handleSendClick}
                disabled={sending || totalSelected === 0}
              >
                <MailOutlined /> {sending ? 'Processing.' : 'Request Document'}
              </InvertFillButton>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Documents Confirmation"
        open={confirmOpen}
        width={560}
        centered
        closable={!sending}
        maskClosable={!sending}
        keyboard={!sending}
        onCancel={() => setConfirmOpen(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <InvertFillButton
              variant="white"
              size="sm"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
            >
              Cancel
            </InvertFillButton>
            <InvertFillButton
              variant="blue"
              size="sm"
              onClick={handleConfirmSend}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send mail'}
            </InvertFillButton>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '8px',
              padding: '12px',
              border: '1px solid #dbe3ef',
              borderRadius: '8px',
              backgroundColor: '#f8fafc',
            }}
          >
            <div>
              <strong style={{ color: '#475569' }}>To</strong>
              <div style={{ marginTop: '2px', overflowWrap: 'anywhere' }}>
                {applicantEmail || 'Applicant email on file'}
              </div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                paddingTop: '8px',
                borderTop: '1px solid #e2e8f0',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <strong style={{ color: '#475569' }}>Order</strong>
                <div style={{ marginTop: '2px', overflowWrap: 'anywhere' }}>
                  {orderDisplayId || orderId}
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <strong style={{ color: '#475569' }}>Member</strong>
                <div style={{ marginTop: '2px', overflowWrap: 'anywhere' }}>
                  {member?.name || '—'}
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              padding: '12px',
              border: '1px solid #dbe3ef',
              borderRadius: '8px',
            }}
          >
            <strong style={{ color: '#475569' }}>Documents ({documentNames.length})</strong>
            <div style={{ marginTop: '4px', lineHeight: 1.6, overflowWrap: 'anywhere' }}>
              {documentNames.join(', ')}
            </div>
          </div>
          {remark.trim() && (
            <div
              style={{
                padding: '12px',
                border: '1px solid #dbe3ef',
                borderRadius: '8px',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              <strong style={{ color: '#475569' }}>Remark</strong>
              <div style={{ marginTop: '4px', lineHeight: 1.6 }}>{remark.trim()}</div>
            </div>
          )}
          {!isAlreadyAdditionalInfo && updateStatus && (
            <div
              style={{
                padding: '10px 12px',
                border: '1px solid #fed7aa',
                borderRadius: '8px',
                backgroundColor: '#fff7ed',
                color: '#9a3412',
              }}
            >
              Application status will be set to {ADDITIONAL_INFO_STATUS}.
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DocumentReminderPanel;
